/** The piste itself ends at a jagged cliff revealed from below the camera. */
const CLIFF_REVEAL_MS = 1650;
const CLIFF_WORLD_SCROLL_MS = 1050;

export class CliffEffect {
  private width: number;
  private height: number;
  private elapsedMs = 0;
  private active = false;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
  }

  begin() { this.elapsedMs = 0; this.active = true; }
  reset() { this.elapsedMs = 0; this.active = false; }
  update(dtMs: number) {
    if (this.active) this.elapsedMs += Math.max(0, Math.min(dtMs, 100));
  }

  getProgress() { return Math.min(1, this.elapsedMs / CLIFF_REVEAL_MS); }

  shouldScrollWorld() { return this.active && this.elapsedMs < CLIFF_WORLD_SCROLL_MS; }

  private edge(progress: number) {
    // Reveal only into the lower half, then lock the cornice in place. From
    // there the skiers move over it; the cliff never sweeps through them.
    const reveal = Math.min(1, progress / 0.18);
    const eased = 1 - Math.pow(1 - reveal, 3);
    const target = Math.min(this.height * 0.66, 270);
    return this.height + 70 - eased * (this.height + 70 - target);
  }

  private edgeY(x: number, base: number) {
    return base
      + Math.sin(x * 0.012) * 4
      + Math.sin(x * 0.031 + 1.3) * 2;
  }

  drawUnder(ctx: CanvasRenderingContext2D) {
    if (!this.active) return;
    const progress = this.getProgress();
    const base = this.edge(progress);
    const left = -20, right = this.width + 20;
    const steps = 22;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(left, this.height + 20);
    ctx.lineTo(left, this.edgeY(left, base));
    for (let i = 0; i <= steps; i++) {
      const x = left + (i / steps) * (right - left);
      ctx.lineTo(x, this.edgeY(x, base));
    }
    ctx.lineTo(right, this.height + 20);
    ctx.closePath();

    const abyss = ctx.createLinearGradient(0, base, 0, this.height);
    abyss.addColorStop(0, '#8eb8ce');
    abyss.addColorStop(0.09, '#345d77');
    abyss.addColorStop(0.30, '#102b3d');
    abyss.addColorStop(1, '#020910');
    ctx.fillStyle = abyss;
    ctx.fill();

    // Vertical ice strata establish that this is a drop, not dark snow.
    ctx.save();
    ctx.clip();
    ctx.globalAlpha = 0.28;
    for (let i = 0; i < 14; i++) {
      const x = left + ((i + 0.5) / 14) * (right - left);
      ctx.strokeStyle = i % 2 ? '#9ac4d8' : '#4f819e';
      ctx.lineWidth = 2 + (i % 3);
      ctx.beginPath();
      ctx.moveTo(x, this.edgeY(x, base) + 12);
      ctx.lineTo(x + Math.sin(i * 2.4) * 16, this.height + 30);
      ctx.stroke();
    }
    ctx.restore();

    // The lip is terrain, so it sits behind the approaching skiers. Keeping it
    // out of the foreground pass prevents bodies phasing beneath the snow.
    ctx.strokeStyle = '#f9fdff';
    ctx.lineWidth = 13;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    for (let i = 0; i <= steps; i++) {
      const x = left + (i / steps) * (right - left);
      const y = this.edgeY(x, base);
      i === 0 ? ctx.moveTo(x,y) : ctx.lineTo(x,y);
    }
    ctx.stroke();
    ctx.strokeStyle = 'rgba(101,145,169,0.76)';
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.restore();
  }

  drawOver(ctx: CanvasRenderingContext2D) {
    if (!this.active) return;
    const progress = this.getProgress();
    // Reveal progress intentionally clamps once the cliff is in position, but
    // debris must keep moving for the full crash presentation. An independent
    // clock prevents the snow chunks freezing when getProgress() reaches 1.
    const animationSeconds = this.elapsedMs / 1000;
    const base = this.edge(progress);
    const left = -20, right = this.width + 20;
    ctx.save();
    // Snow chunks break from the lip and fall into the void.
    ctx.globalAlpha = Math.min(0.9, progress * 3);
    for (let i = 0; i < 28; i++) {
      const x = left + ((i * 0.618) % 1) * (right - left);
      const fall = (animationSeconds * (0.48 + (i % 5) * 0.035) + i * 0.091) % 1;
      const y = this.edgeY(x, base) + 8 + fall * fall * 150;
      ctx.fillStyle = i % 3 ? '#f7fcff' : '#a9ccde';
      ctx.beginPath();
      ctx.arc(x, y, 2.5 + (i % 5) * 1.2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}
